import AsyncStorage from "@react-native-async-storage/async-storage";
import { IProductHint, PRODUCT_HINTS } from "@/constants/hints";

export const STORAGE_KEY_HINTS_FIRST_SEEN = "@memories/hints_first_seen_timestamp";
export const STORAGE_KEY_HINTS_DISMISSED_VERSION = "@memories/hints_dismissed_version";
export const STORAGE_KEY_HINTS_ENABLED = "@memories/hints_enabled";

/** Two days in milliseconds: 48 hours */
export const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * Checks whether a given timestamp is within the initial two-day onboarding window.
 * @param {number} firstSeenTimestamp - Timestamp when hints were first viewed.
 * @param {number} [currentTimestamp=Date.now()] - Current epoch timestamp.
 * @returns {boolean} True if within the first 48 hours.
 */
export function isWithinFirstTwoDays(
  firstSeenTimestamp: number,
  currentTimestamp: number = Date.now()
): boolean {
  if (!firstSeenTimestamp || firstSeenTimestamp <= 0) return true;
  return currentTimestamp - firstSeenTimestamp < TWO_DAYS_MS;
}

/**
 * Retrieves the timestamp when hints were first seen, initializing it if not yet set.
 * @returns {Promise<number>} Epoch milliseconds.
 */
export async function getOrInitFirstSeenTimestamp(): Promise<number> {
  try {
    const stored = await AsyncStorage.getItem(STORAGE_KEY_HINTS_FIRST_SEEN);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    const now = Date.now();
    await AsyncStorage.setItem(STORAGE_KEY_HINTS_FIRST_SEEN, String(now));
    return now;
  } catch (err) {
    console.warn("[HintsService] Error accessing first seen timestamp:", err);
    return Date.now();
  }
}

/**
 * Checks if the hint bar has been dismissed for the currently running app version.
 * @param {string} currentVersion - The active app version.
 * @returns {Promise<boolean>} True if dismissed for this specific version.
 */
export async function isHintsDismissedForVersion(currentVersion: string): Promise<boolean> {
  try {
    const dismissedVersion = await AsyncStorage.getItem(STORAGE_KEY_HINTS_DISMISSED_VERSION);
    return dismissedVersion !== null && dismissedVersion === currentVersion;
  } catch (err) {
    console.warn("[HintsService] Error checking dismissed version:", err);
    return false;
  }
}

/**
 * Dismisses the hint bar, storing the current app version so it stays hidden until the next update.
 * @param {string} currentVersion - The active app version to stamp.
 * @returns {Promise<void>}
 */
export async function dismissHintsForCurrentVersion(currentVersion: string): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY_HINTS_DISMISSED_VERSION, currentVersion);
  } catch (err) {
    console.warn("[HintsService] Error dismissing hints:", err);
  }
}

/**
 * Resets the dismissed state so hints will reappear on the next app render.
 * @returns {Promise<void>}
 */
export async function resetHintsDismissal(): Promise<void> {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY_HINTS_DISMISSED_VERSION);
  } catch (err) {
    console.warn("[HintsService] Error resetting dismissed version:", err);
  }
}

/**
 * In-memory flag tracking whether hints have been dismissed during the current page load / app run.
 * Purely in-memory so whenever the site is reloaded, refreshed, or re-visited, hints show again.
 */
let hintsDismissedInSession: boolean = false;

// Cleanup any legacy sessionStorage flag to guarantee reloads and refreshes show hints immediately
if (typeof window !== "undefined" && window.sessionStorage) {
  try {
    window.sessionStorage.removeItem("@memories/hints_session_dismissed");
  } catch {
    // ignore
  }
}

/**
 * Checks whether hints were dismissed during the current page run.
 * @returns {boolean} True if hints were closed/dismissed during this page run.
 */
export function isHintsDismissedInSession(): boolean {
  return hintsDismissedInSession;
}

/**
 * Marks hints as dismissed for the current page run only.
 * They remain hidden while navigating in the SPA, but show again when the site is reloaded, refreshed, or re-visited.
 */
export function dismissHintsForSession(): void {
  hintsDismissedInSession = true;
}

/**
 * Resets the in-session dismissal state (primarily for tests).
 */
export function resetHintsSessionDismissal(): void {
  hintsDismissedInSession = false;
  if (typeof window !== "undefined" && window.sessionStorage) {
    try {
      window.sessionStorage.removeItem("@memories/hints_session_dismissed");
    } catch {
      // ignore
    }
  }
}

/**
 * Checks whether product hints are globally enabled by user preference.
 * Defaults to true.
 * @returns {Promise<boolean>}
 */
export async function isHintsGloballyEnabled(): Promise<boolean> {
  try {
    const val = await AsyncStorage.getItem(STORAGE_KEY_HINTS_ENABLED);
    if (val === null) return true;
    return val === "true";
  } catch (err) {
    console.warn("[HintsService] Error reading hints enabled state:", err);
    return true;
  }
}

/**
 * Updates the global preference for displaying product hints.
 * @param {boolean} enabled - Whether hints should be enabled.
 * @returns {Promise<void>}
 */
export async function setHintsGloballyEnabled(enabled: boolean): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY_HINTS_ENABLED, enabled ? "true" : "false");
  } catch (err) {
    console.warn("[HintsService] Error saving hints enabled state:", err);
  }
}

/**
 * Selects which hints to display according to the 2-day cadence rule.
 * During the first 2 days, returns all hints.
 * Afterwards, returns an array containing a single randomly chosen hint.
 * @param {IProductHint[]} allHints - The full catalog of product hints.
 * @param {boolean} isFirstTwoDays - Whether current session is within the initial 48 hours.
 * @param {number} [randomSeed] - Optional index seed for deterministic testing.
 * @returns {{ hints: IProductHint[]; isMulti: boolean }}
 */
export function resolveDisplayHints(
  allHints: IProductHint[] = PRODUCT_HINTS,
  isFirstTwoDays: boolean,
  randomSeed?: number
): { hints: IProductHint[]; isMulti: boolean } {
  if (allHints.length === 0) {
    return { hints: [], isMulti: false };
  }

  if (isFirstTwoDays) {
    return { hints: allHints, isMulti: true };
  }

  const selectedIndex =
    randomSeed !== undefined
      ? Math.abs(randomSeed) % allHints.length
      : Math.floor(Math.random() * allHints.length);

  return {
    hints: [allHints[selectedIndex]],
    isMulti: false,
  };
}
